/**
 * One player's window onto the world: a screen-space container placed at a
 * rectangle of the canvas and clipped to it, a world container inside it
 * scaled by its own camera, and the popups that belong to that player. A
 * single player has one viewport over the whole canvas; a split-screen race
 * stacks one per player. The world's statics are drawn once per viewport (a
 * course is a few hundred sprites), so each half is an independent scene.
 */

import { Container, Graphics } from "pixi.js";
import { Camera } from "./camera";
import type { RenderFeel } from "./feel";
import { Popups } from "./popups";

export class Viewport {
  /** Screen space, positioned at the viewport's rectangle; the mask clips to it. */
  readonly root = new Container();
  /** Screen space behind the world: the night sky and its parallax layers (`Backdrop`). */
  readonly backdrop = new Container();
  /** World space (meters, y up), scaled and placed by the camera each frame. */
  readonly world = new Container();
  readonly popups = new Popups();
  readonly camera = new Camera();
  private readonly clip = new Graphics();
  x = 0;
  y = 0;
  width = 1;
  height = 1;

  constructor() {
    this.root.addChild(this.clip);
    this.root.addChild(this.backdrop);
    this.root.addChild(this.world);
    this.root.addChild(this.popups.root);
    this.clip.visible = false;
  }

  /**
   * Place the viewport on the canvas and size its camera. `viewWidth` meters span the rectangle's
   * longer side, so a tall rectangle (a phone held upright) keeps the scale of a wide one. `clip`
   * masks the drawing to the rectangle (a stencil mask; a viewport that fills the canvas needs
   * none, and software GL pays a one-time hitch for the stencil, so it is only on when asked).
   */
  setRect(
    x: number,
    y: number,
    width: number,
    height: number,
    viewWidth: number,
    feel: RenderFeel,
    clip = true,
  ): void {
    this.x = x;
    this.y = y;
    this.width = Math.max(1, width);
    this.height = Math.max(1, height);
    this.root.position.set(x, y);
    this.clip.clear().rect(0, 0, this.width, this.height).fill(0xffffff);
    this.clip.visible = clip;
    this.root.mask = clip ? this.clip : null;
    const across = (viewWidth * this.width) / Math.max(this.width, this.height);
    this.camera.resize(this.width, this.height, { ...feel, viewWidth: across });
  }

  /** Scale and place the world container from the camera, after the camera has followed its view. */
  applyCamera(): void {
    const c = this.camera;
    this.world.scale.set(c.ppm, -c.ppm);
    this.world.position.set(c.toScreenX(0), c.toScreenY(0));
  }

  destroy(): void {
    this.root.parent?.removeChild(this.root);
    this.root.destroy({ children: true, texture: false, textureSource: false });
  }
}
